import React, { useContext, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { Modal, Typography, Box, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import {
  GridToolbarContainer,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid-pro"

import i18n from "i18n"
import TemplatesContext from "context/templates/templatesContext"
import AuthContext from "context/auth/authContext"
import ProjectContext from "context/projects/projectContext"
import ModalWaiting from "./modalWaiting"
import { PLANHOPPER_ORG_ID } from "constants/common"
import DataGridLocaleText from "common/DataGridLocaleText"
import AccordionWithDataGridPro from "common/AccordionWithDataGridPro"

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 900,
  minHeight: 600,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
}

const CustomToolbar = () => (
  <GridToolbarContainer>
    <GridToolbarQuickFilter
      slotProps={{
        textField: {
          inputProps: { "data-testid": "template-select-quick-filter" },
        },
      }}
    />
  </GridToolbarContainer>
)

const ModalSelectPlantilla = ({ open, setOpen, imInBudget }) => {
  const navigate = useNavigate()
  const [openWaiting, setOpenWaiting] = useState(false)
  const [loading, setLoading] = useState(false)

  const { getTemplates, templates } = useContext(TemplatesContext)
  const { user, getUser } = useContext(AuthContext)
  const {
    importPlantilla2Project,
    selectedProject,
    createProjectWithTemplate,
    projectInfo,
    clearUploadProcess,
  } = useContext(ProjectContext)

  useEffect(() => {
    if (!user) getUser()
    else getTemplates(user["custom:organizationId"], true)
  }, [user])

  const handleSelectTemplate = async template => {
    setLoading(true)
    setOpenWaiting(true)
    try {
      if (imInBudget) {
        await importPlantilla2Project(
          projectInfo.projectId,
          template.templateId,
        )
      } else {
        const { newProjectId } = await createProjectWithTemplate({
          organizationId: user?.["custom:organizationId"],
          projectId: template.projectId,
          templateId: template.templateId,
          is_template: false,
        })
        setOpenWaiting(false)
        setOpen(false)
        clearUploadProcess()
        if (newProjectId) {
          navigate(`/budgeting/${newProjectId}`)
          window.location.reload()
        }
      }
    } catch {
      setOpenWaiting(false)
    } finally {
      setLoading(false)
    }
  }

  const columns = [
    {
      field: "projectImage",
      headerName: i18n.t("dashboard.image"),
      flex: 0.3,
      minWidth: 80,
      renderCell: params => (
        <img
          src={params.value}
          alt="Proyecto"
          style={{ width: 40, height: 40, objectFit: "cover" }}
        />
      ),
      sortable: false,
      filterable: false,
    },
    {
      field: "name",
      headerName: i18n.t("dashboard.name"),
      width: 200,
      flex: 1,
      minWidth: 150,
    },
    {
      field: "projectId",
      headerName: "ID",
      width: 0,
    },
  ]

  const datagridProps = {
    columns,
    slots: { toolbar: CustomToolbar },
    getRowId: row => row.projectId,
    rowHeight: 45,
    columnVisibilityModel: { projectId: false },
    hideFooter: true,
    onRowClick: ({ row }) => handleSelectTemplate(row),
    localeText: DataGridLocaleText(),
    sx: {
      "& .MuiDataGrid-columnHeaders": {
        position: "sticky",
        top: 0,
        backgroundColor: "#f5f5f5",
        zIndex: 1,
      },
    },
  }

  return (
    <>
      <Modal open={open} onClose={() => null}>
        <Box data-testid="template-select-modal" sx={modalStyle}>
          <Box sx={{ marginBottom: 2, textAlign: "left" }}>
            <IconButton
              data-testid="template-select-modal-close"
              sx={{ position: "absolute", top: 8, right: 8 }}
              onClick={() => setOpen(false)}
              size="large"
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <Typography variant="h6" align="center" fontSize="20px" mb={2}>
              {i18n.t("Selecciona la plantilla de referencia que desees")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <AccordionWithDataGridPro
                accordionDataTestId="template-select-accordion-my-templates"
                dataGridDataTestId="template-select-grid-my-templates"
                title={i18n.t("Mis Plantillas")}
                rows={templates.filter(
                  template => template.organizationId !== PLANHOPPER_ORG_ID,
                )}
                dataGridProps={datagridProps}
              />

              <AccordionWithDataGridPro
                accordionDataTestId="template-select-accordion-planhopper-templates"
                dataGridDataTestId="template-select-grid-planhopper-templates"
                title={i18n.t("Plantillas de Planhopper")}
                rows={templates.filter(
                  template => template.organizationId === PLANHOPPER_ORG_ID,
                )}
                dataGridProps={datagridProps}
              />
            </Box>
          </Box>
        </Box>
      </Modal>

      {openWaiting && (
        <ModalWaiting
          open={openWaiting}
          setOpen={setOpenWaiting}
          setOpenAnt={setOpen}
          project={imInBudget ? projectInfo : selectedProject}
          loading={loading}
          imInBudget={imInBudget}
        />
      )}
    </>
  )
}

ModalSelectPlantilla.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  imInBudget: PropTypes.bool,
}

export default ModalSelectPlantilla
